/**
 * GeoHash — public-domain global geocoding system (Niemeyer 2008).
 * Spec: en.wikipedia.org/wiki/Geohash
 *
 * Algorithm:
 *  - Base-32 encoding (alphabet excludes a, i, l, o)
 *  - Each character = 5 bits
 *  - Bits alternate between longitude (first) and latitude
 *  - Each bit halves the range it operates on
 *  - Length controls precision (5 ≈ 5km, 8 ≈ 38m, 12 ≈ 3.7cm)
 */

import type { LatLng } from "./index.js";

const GH_ALPHABET = "0123456789bcdefghjkmnpqrstuvwxyz"; // 32 chars
const GH_BITS_PER_CHAR = 5;

export const GEOHASH_MIN_LENGTH = 1;
export const GEOHASH_MAX_LENGTH = 12;

export type GeohashCell = {
  readonly code: string;
  readonly center: LatLng;
  readonly bounds: {
    readonly minLat: number;
    readonly maxLat: number;
    readonly minLon: number;
    readonly maxLon: number;
  };
  readonly length: number;
};

const CHAR_INDEX: Record<string, number> = {};
for (let i = 0; i < GH_ALPHABET.length; i++) {
  CHAR_INDEX[GH_ALPHABET[i]!] = i;
}

/**
 * Encode lat/lng → GeoHash at given length.
 * Default length = 9 (≈4.8m × 4.8m cell), comparable to DIGIPIN's 10-char precision.
 */
export function encodeGeohash(
  lat: number,
  lng: number,
  length: number = 9,
): string {
  if (lat < -90 || lat > 90) {
    throw new RangeError(`Latitude ${lat} out of range [-90, 90]`);
  }
  if (lng < -180 || lng > 180) {
    throw new RangeError(`Longitude ${lng} out of range [-180, 180]`);
  }
  if (length < GEOHASH_MIN_LENGTH || length > GEOHASH_MAX_LENGTH) {
    throw new RangeError(`Length ${length} out of range [1, 12]`);
  }

  let minLat = -90;
  let maxLat = 90;
  let minLon = -180;
  let maxLon = 180;

  let code = "";
  let bit = 0;
  let charBits = 0;
  let isEvenBit = true; // even bits → longitude, odd bits → latitude

  while (code.length < length) {
    if (isEvenBit) {
      const mid = (minLon + maxLon) / 2;
      if (lng >= mid) {
        charBits = (charBits << 1) | 1;
        minLon = mid;
      } else {
        charBits = charBits << 1;
        maxLon = mid;
      }
    } else {
      const mid = (minLat + maxLat) / 2;
      if (lat >= mid) {
        charBits = (charBits << 1) | 1;
        minLat = mid;
      } else {
        charBits = charBits << 1;
        maxLat = mid;
      }
    }
    isEvenBit = !isEvenBit;
    bit++;

    if (bit === GH_BITS_PER_CHAR) {
      code += GH_ALPHABET[charBits];
      bit = 0;
      charBits = 0;
    }
  }

  return code;
}

/**
 * Decode GeoHash → cell info (centre + bounds + length).
 */
export function decodeGeohash(code: string): GeohashCell {
  const lower = code.toLowerCase();
  if (lower.length < GEOHASH_MIN_LENGTH || lower.length > GEOHASH_MAX_LENGTH) {
    throw new Error(`Invalid GeoHash length: ${lower.length}`);
  }

  let minLat = -90;
  let maxLat = 90;
  let minLon = -180;
  let maxLon = 180;
  let isEvenBit = true;

  for (const ch of lower) {
    const idx = CHAR_INDEX[ch];
    if (idx === undefined) {
      throw new Error(`Invalid GeoHash character '${ch}'`);
    }
    for (let bit = 4; bit >= 0; bit--) {
      const bitVal = (idx >> bit) & 1;
      if (isEvenBit) {
        const mid = (minLon + maxLon) / 2;
        if (bitVal === 1) minLon = mid;
        else maxLon = mid;
      } else {
        const mid = (minLat + maxLat) / 2;
        if (bitVal === 1) minLat = mid;
        else maxLat = mid;
      }
      isEvenBit = !isEvenBit;
    }
  }

  return {
    code: lower,
    center: {
      lat: (minLat + maxLat) / 2,
      lng: (minLon + maxLon) / 2,
    },
    bounds: { minLat, maxLat, minLon, maxLon },
    length: lower.length,
  };
}

/**
 * True if a string is a syntactically valid GeoHash.
 */
export function isValidGeohash(code: string): boolean {
  const lower = code.toLowerCase();
  if (lower.length < GEOHASH_MIN_LENGTH || lower.length > GEOHASH_MAX_LENGTH) return false;
  for (const ch of lower) {
    if (CHAR_INDEX[ch] === undefined) return false;
  }
  return true;
}
